import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "components/app/dialog/dialog";
import SelectRadioMultipleCheck from "components/app/function/SelectRadioMultipleTick";
import SearchBar from "components/app/search-bar/SearchBar";
import { Button } from "components/ui/button";
import { LoaderCircle, X } from "lucide-react";
import { AppRecord, AppRecordStatus } from "models/appRecord";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch } from "react-redux";
import { AppLog } from "services";
import { setProfessionalProfileLoading } from "store/profileDetailSlice";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";

interface DialogProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (value: boolean) => void;
  userProfileDetail: any;
  setUserProfileDetail: (value: object) => void;
}

export default function DailogProfessional({ isDialogOpen, userProfileDetail, setIsDialogOpen, setUserProfileDetail }: DialogProps) {
  const [textInput, setTextInput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [formattedItems, setFormattedItems] = useState<string[]>([]);
  const [professionalData, setProfessionalData] = useState<any>();
  const [submitValue, setSubmitValue] = useState<any>([]);
  const dispatch = useDispatch();
  const { professionalProfileState } = useAppSelector((state) => state.profileDetail);
  const { token } = useAppSelector((state) => state.auth);

  const submitProfessional = async (value: any) => {
    try {
      const url = `${RouteApi.submitUpdateUserProfessional}token=${token}`;
      const data = {
        professionals: value,
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        const updateUser = {
          ...userProfileDetail.user,
          professional: response.user.professional,
        };

        localStorage.setItem("user", JSON.stringify(response.user));

        setUserProfileDetail((prevState: any) => ({
          ...prevState,
          user: updateUser,
        }));
      }
    } catch (error) {
      AppLog.show({
        message: `[submitProfessional]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const handleSave = () => {
    if (userProfileDetail.user.professional !== submitValue) {
      submitProfessional(submitValue.split(",").map((item: any) => item.trim()));
    }
    setIsDialogOpen(false);
  };

  const getProfessional = async () => {
    if (loading) return;
    try {
      setLoading(true);
      const url = `${RouteApi.getProfessional}token=${token}`;
      const data = { search_text: textInput };
      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        setProfessionalData(response.professionals);
        const items = response.professionals?.data.map((item: any) => item.description) || [];
        setFormattedItems(items);
      }
    } catch (error) {
      AppLog.show({
        message: `[getProfessional]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchMoreProfessionalContent = async ({ next }: { next: string }) => {
    dispatch(
      setProfessionalProfileLoading({
        ...professionalProfileState,
        loadingStatus: AppRecordStatus.loadingMore,
      })
    );

    await RequestApi(next).then((responses) => {
      if (responses.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: responses["professionals"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: responses["professionals"]["next_page_url"] != null ? AppRecordStatus.loaded : AppRecordStatus.noLoadMore,
        };

        const tmpPosts = [...professionalData["data"], ...responses["professionals"]["data"]];
        const newPost = { ...professionalData, data: tmpPosts };
        setFormattedItems(newPost);
        dispatch(setProfessionalProfileLoading(stateRecord));
      }
    });
  };

  useEffect(() => {
    if (userProfileDetail.user.user_type === "Personal") {
      getProfessional();
    }
  }, [textInput]);

  useEffect(() => {
    if (isDialogOpen && isFetching) {
      getProfessional();
    }
  }, [isDialogOpen, isFetching]);

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent isShowBtnXClose={false} className="custom-dialog-class">
        <DialogHeader className="pb-10">
          <DialogTitle>Edit Professional</DialogTitle>
          <DialogDescription>Make changes to your profile here. Click save when you're done.</DialogDescription>
          <SearchBar className="absolute top-16 w-full left-0 px-4" onValueChange={(value) => setTextInput(value)} />
          <div
            className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent w-[28px] h-[28px]"
            onClick={() => setIsDialogOpen(false)}
          >
            <X />
          </div>
        </DialogHeader>
        <div>
          {professionalData ? (
            <InfiniteScroll
              style={{ height: "300px" }}
              className={``}
              dataLength={professionalData["data"]?.length}
              next={() =>
                fetchMoreProfessionalContent({
                  next: professionalProfileState.nextPageUrl,
                })
              }
              hasMore={professionalProfileState.nextPageUrl === "" ? false : true}
              loader={
                <div className="w-full flex justify-center mt-2">
                  <LoaderCircle size={30} className="animate-spin text-yellow-400" />
                </div>
              }
              endMessage={
                <p className="w-full text-center mt-2">
                  <b>Nothing more to load!</b>
                </p>
              }
            >
              <SelectRadioMultipleCheck
                isSplitWithComma={true}
                value={userProfileDetail.user.professional}
                items={formattedItems}
                onValueChange={(value: any) => setSubmitValue(value)}
              />
            </InfiniteScroll>
          ) : (
            <></>
          )}
        </div>
        <DialogFooter>
          <Button className="bg-destructive text-white bg-blue-600 hover:bg-blue-500" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
